import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../../config/api';
import { getText } from '../../utils/language';
import { useTheme } from '../../context/ThemeContext';

const AdminDashboard = () => {
  const { isDarkMode } = useTheme();
  const [stats, setStats] = useState({
    totalArtworks: 0,
    pendingOrders: 0,
    completedOrders: 0,
    totalRevenue: 0,
    recentOrders: [],
    popularArtworks: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        // Fetch dashboard statistics from the API
        const response = await apiService.get('/admin/dashboard/stats');
        // Ensure arrays are always present, even if API returns null/undefined
        setStats({
          totalArtworks: response.totalArtworks || 0,
          pendingOrders: response.pendingOrders || 0,
          completedOrders: response.completedOrders || 0,
          totalRevenue: response.totalRevenue || 0,
          recentOrders: Array.isArray(response.recentOrders) ? response.recentOrders : [],
          popularArtworks: Array.isArray(response.popularArtworks) ? response.popularArtworks : []
        });
        setLoading(false);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setLoading(false);
      }
    };
    
    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="text-center py-10">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto"></div>
        <p className={`mt-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Loading dashboard data...</p>
      </div>
    );
  }

  return (
    <div>
      <h1 className={`text-2xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-primary'}`}>Admin Dashboard</h1>
      
      {/* Stats Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className={`p-6 rounded-islamic shadow-sm border-l-4 border-primary ${isDarkMode ? 'bg-gray-700' : 'bg-white'}`}>
          <div className="flex justify-between items-center">
            <div>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Artworks</p>
              <p className="text-2xl font-bold text-primary">{stats.totalArtworks}</p>
            </div>
            <div className="text-primary bg-primary/10 p-3 rounded-full">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
          <Link to="/admin/artworks" className="text-sm text-primary hover:underline mt-4 inline-block">
            Manage Artworks →
          </Link>
        </div>
        
        <div className={`p-6 rounded-islamic shadow-sm border-l-4 border-yellow-500 ${isDarkMode ? 'bg-gray-700' : 'bg-white'}`}>
          <div className="flex justify-between items-center">
            <div>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Pending Orders</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.pendingOrders}</p>
            </div>
            <div className={`text-yellow-500 p-3 rounded-full ${isDarkMode ? 'bg-yellow-900/20' : 'bg-yellow-50'}`}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <Link to="/admin/orders" className="text-sm text-primary hover:underline mt-4 inline-block">
            View Orders →
          </Link>
        </div>
        
        <div className={`p-6 rounded-islamic shadow-sm border-l-4 border-green-500 ${isDarkMode ? 'bg-gray-700' : 'bg-white'}`}>
          <div className="flex justify-between items-center">
            <div>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Completed Orders</p>
              <p className="text-2xl font-bold text-green-600">{stats.completedOrders}</p>
            </div>
            <div className={`text-green-500 p-3 rounded-full ${isDarkMode ? 'bg-green-900/20' : 'bg-green-50'}`}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
          <Link to="/admin/orders" className="text-sm text-primary hover:underline mt-4 inline-block">
            View All →
          </Link>
        </div>
        
        <div className={`p-6 rounded-islamic shadow-sm border-l-4 border-accent ${isDarkMode ? 'bg-gray-700' : 'bg-white'}`}>
          <div className="flex justify-between items-center">
            <div>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Revenue</p>
              <p className="text-2xl font-bold text-accent">${(stats.totalRevenue || 0).toLocaleString()}</p>
            </div>
            <div className="text-accent bg-accent/10 p-3 rounded-full">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <span className={`text-sm mt-4 inline-block ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Last 30 days
          </span>
        </div>

        <div className={`p-6 rounded-islamic shadow-sm border-l-4 border-purple-500 ${isDarkMode ? 'bg-gray-700' : 'bg-white'}`}>
          <div className="flex justify-between items-center">
            <div>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Printify Store</p>
              <p className={`text-2xl font-bold ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>Manage</p>
            </div>
            <div className={`p-3 rounded-full ${isDarkMode ? 'bg-purple-900/20 text-purple-400' : 'bg-purple-50 text-purple-600'}`}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
            </div>
          </div>
          <Link to="/admin/printify" className="text-sm text-primary hover:underline mt-4 inline-block">
            Manage Products →
          </Link>
        </div>
      </div>
      
      {/* Recent Orders */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-primary'}`}>Recent Orders</h2>
          <Link to="/admin/orders" className="text-sm text-primary hover:underline">
            View All →
          </Link>
        </div>
        <div className={`overflow-hidden shadow-sm rounded-islamic ${isDarkMode ? 'bg-gray-700' : 'bg-white'}`}>
          <table className={`min-w-full divide-y ${isDarkMode ? 'divide-gray-600' : 'divide-gray-200'}`}>
            <thead className={isDarkMode ? 'bg-gray-600' : 'bg-gray-50'}>
              <tr>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                  Order ID
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                  Customer
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                  Date
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                  Amount
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                  Status
                </th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDarkMode ? 'bg-gray-700 divide-gray-600' : 'bg-white divide-gray-200'}`}>
              {stats.recentOrders.length > 0 ? (
                stats.recentOrders.map((order) => (
                  <tr key={order._id} className={isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-primary">
                      {order._id.substring(0, 8)}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {order.customer?.name || 'Unknown'}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {new Date(order.createdAt).toLocaleDateString()}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      ${order.totalAmount.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                        ${order.status === 'Delivered' ? 'bg-green-100 text-green-800' : 
                          order.status === 'Processing' ? 'bg-yellow-100 text-yellow-800' : 
                          order.status === 'Shipped' ? 'bg-blue-100 text-blue-800' :
                          'bg-red-100 text-red-800'}`}>
                        {order.status}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className={`px-6 py-4 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    No recent orders found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Popular Artworks */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-primary'}`}>Popular Artworks</h2>
          <Link to="/admin/artworks" className="text-sm text-primary hover:underline">
            View All →
          </Link>
        </div>
        <div className={`overflow-hidden shadow-sm rounded-islamic ${isDarkMode ? 'bg-gray-700' : 'bg-white'}`}>
          <table className={`min-w-full divide-y ${isDarkMode ? 'divide-gray-600' : 'divide-gray-200'}`}>
            <thead className={isDarkMode ? 'bg-gray-600' : 'bg-gray-50'}>
              <tr>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                  Artwork ID
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                  Title
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                  Total Sales
                </th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDarkMode ? 'bg-gray-700 divide-gray-600' : 'bg-white divide-gray-200'}`}>
              {stats.popularArtworks.length > 0 ? (
                stats.popularArtworks.map((artwork) => (
                  <tr key={artwork._id} className={isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-primary">
                      {artwork._id.substring(0, 8)}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {getText(artwork.title, 'en')}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {artwork.totalSold}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className={`px-6 py-4 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    No sales data available yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;